import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { EnvVarDto, StorageDto } from '@cpm/shared';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { api } from '../api/client.js';
import { Modal } from './Modal.js';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorNote,
  Input,
  Label,
  Spinner,
  Textarea,
} from './ui.js';

/**
 * Environment-variable and persistent-storage panels shared by the
 * application, database and service detail pages — the API exposes the
 * same endpoint pattern for all three (`<apiBase>/envs`, `<apiBase>/storages`),
 * so the UI does too. `apiBase` is e.g. `/api/databases/<id>`; `queryKey`
 * scopes react-query caching per resource.
 */

export function FeatureNotice({ feature }: { feature: string }) {
  return (
    <Card>
      <EmptyState
        title="Not included in this plan"
        description={`The subscription's plan does not include the "${feature}" feature. Upgrade the plan or add a subscription override to enable it.`}
      />
    </Card>
  );
}

// ── Environment variables ────────────────────────────────────────────

function EnvVarForm({
  apiBase,
  queryKey,
  existing,
  onDone,
}: {
  apiBase: string;
  queryKey: unknown[];
  existing?: EnvVarDto;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const [key, setKey] = useState(existing?.key ?? '');
  const [value, setValue] = useState(existing?.value ?? '');
  const [isLiteral, setIsLiteral] = useState(existing?.isLiteral ?? false);
  const [isShownOnce, setIsShownOnce] = useState(existing?.isShownOnce ?? false);

  const save = useMutation({
    mutationFn: () => {
      const payload = { key, value, isLiteral, isShownOnce };
      return existing
        ? api.patch(`${apiBase}/envs`, payload)
        : api.post(`${apiBase}/envs`, payload);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey });
      onDone();
    },
  });

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <div>
        <Label htmlFor="env-key">Key</Label>
        <Input
          id="env-key"
          required
          disabled={Boolean(existing)}
          value={key}
          onChange={(e) => setKey(e.target.value)}
          placeholder="DATABASE_URL"
        />
      </div>
      <div>
        <Label htmlFor="env-value">Value</Label>
        <Textarea
          id="env-value"
          rows={3}
          className="font-mono text-xs"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={existing?.isShownOnce ? 'Hidden — enter a new value to replace it' : ''}
        />
      </div>
      <div className="flex flex-wrap gap-4 text-sm">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            className="size-4 rounded border-slate-300 text-brand-600 focus:ring-brand-600"
            checked={isLiteral}
            onChange={(e) => setIsLiteral(e.target.checked)}
          />
          Literal value (no interpolation)
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            className="size-4 rounded border-slate-300 text-brand-600 focus:ring-brand-600"
            checked={isShownOnce}
            onChange={(e) => setIsShownOnce(e.target.checked)}
          />
          Hide value after saving
        </label>
      </div>
      {save.error != null && <ErrorNote error={save.error} />}
      <div className="flex justify-end">
        <Button type="submit" loading={save.isPending}>
          {existing ? 'Save value' : 'Add variable'}
        </Button>
      </div>
    </form>
  );
}

function BulkEnvForm({
  apiBase,
  queryKey,
  onDone,
}: {
  apiBase: string;
  queryKey: unknown[];
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const [text, setText] = useState('');
  const entries = text
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line && !line.startsWith('#'))
    .map((line) => {
      const eq = line.indexOf('=');
      return eq === -1 ? null : { key: line.slice(0, eq).trim(), value: line.slice(eq + 1) };
    })
    .filter((e): e is { key: string; value: string } => e !== null && e.key !== '');

  const save = useMutation({
    mutationFn: () => api.patch(`${apiBase}/envs/bulk`, { data: entries }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey });
      onDone();
    },
  });

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <div>
        <Label htmlFor="env-bulk">One KEY=VALUE per line</Label>
        <Textarea
          id="env-bulk"
          rows={8}
          className="font-mono text-xs"
          placeholder={'NODE_ENV=production\nPORT=3000'}
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
          {entries.length} variable{entries.length === 1 ? '' : 's'} will be created or updated;
          existing variables not listed here are untouched.
        </p>
      </div>
      {save.error != null && <ErrorNote error={save.error} />}
      <div className="flex justify-end">
        <Button type="submit" disabled={entries.length === 0} loading={save.isPending}>
          Apply {entries.length || ''}
        </Button>
      </div>
    </form>
  );
}

export function EnvironmentPanel({
  apiBase,
  queryKey,
  enabled,
  featureEnabled,
  description = 'Applied on the next deployment',
}: {
  /** API prefix, e.g. `/api/databases/<id>`. */
  apiBase: string;
  /** react-query key scoping this resource's env list. */
  queryKey: unknown[];
  /** Whether the resource is linked (queries pause otherwise). */
  enabled: boolean;
  /** The plan's environment_variables feature. */
  featureEnabled: boolean;
  description?: string;
}) {
  const queryClient = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [editing, setEditing] = useState<EnvVarDto | null>(null);

  const envs = useQuery({
    queryKey,
    queryFn: () => api.get<EnvVarDto[]>(`${apiBase}/envs`),
    enabled: featureEnabled && enabled,
  });
  const remove = useMutation({
    mutationFn: (envUuid: string) => api.delete(`${apiBase}/envs/${envUuid}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey }),
  });

  if (!featureEnabled) return <FeatureNotice feature="environment_variables" />;
  if (envs.isPending) return <Spinner />;
  if (envs.error) return <ErrorNote error={envs.error} />;

  return (
    <Card>
      <CardHeader
        title="Environment variables"
        description={description}
        actions={
          <>
            <Button variant="secondary" onClick={() => setBulkOpen(true)}>
              Bulk edit
            </Button>
            <Button onClick={() => setAddOpen(true)}>
              <Plus className="size-4" /> Add variable
            </Button>
          </>
        }
      />
      {remove.error != null && (
        <div className="px-5 pt-3">
          <ErrorNote error={remove.error} />
        </div>
      )}
      {envs.data.length === 0 ? (
        <EmptyState title="No environment variables" />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500 dark:border-slate-800 dark:text-slate-400">
              <tr>
                <th className="px-5 py-3 font-medium">Key</th>
                <th className="px-5 py-3 font-medium">Value</th>
                <th className="px-5 py-3 font-medium">Flags</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {envs.data.map((env) => (
                <tr key={env.uuid}>
                  <td className="px-5 py-3 font-mono text-xs font-medium">{env.key}</td>
                  <td className="max-w-sm truncate px-5 py-3 font-mono text-xs text-slate-600 dark:text-slate-300">
                    {env.isShownOnce ? '••••••••' : env.value}
                  </td>
                  <td className="px-5 py-3">
                    <span className="flex gap-1">
                      {env.isShownOnce && <Badge tone="yellow">hidden</Badge>}
                      {env.isLiteral && <Badge>literal</Badge>}
                      {env.isMultiline && <Badge>multiline</Badge>}
                      {env.isPreview && <Badge tone="blue">preview</Badge>}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-5 py-3 text-right">
                    <Button variant="ghost" onClick={() => setEditing(env)} aria-label={`Edit ${env.key}`}>
                      <Pencil className="size-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      className="hover:!text-red-600"
                      aria-label={`Delete ${env.key}`}
                      onClick={() => {
                        if (window.confirm(`Delete ${env.key}?`)) remove.mutate(env.uuid);
                      }}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Modal title="Add environment variable" open={addOpen} onClose={() => setAddOpen(false)}>
        <EnvVarForm apiBase={apiBase} queryKey={queryKey} onDone={() => setAddOpen(false)} />
      </Modal>
      <Modal title={`Edit ${editing?.key ?? ''}`} open={editing !== null} onClose={() => setEditing(null)}>
        {editing && (
          <EnvVarForm apiBase={apiBase} queryKey={queryKey} existing={editing} onDone={() => setEditing(null)} />
        )}
      </Modal>
      <Modal title="Bulk edit variables" open={bulkOpen} onClose={() => setBulkOpen(false)} wide>
        <BulkEnvForm apiBase={apiBase} queryKey={queryKey} onDone={() => setBulkOpen(false)} />
      </Modal>
    </Card>
  );
}

// ── Persistent storage ───────────────────────────────────────────────

function StorageForm({
  apiBase,
  queryKey,
  existing,
  onDone,
}: {
  apiBase: string;
  queryKey: unknown[];
  existing?: StorageDto;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(existing?.name ?? '');
  const [mountPath, setMountPath] = useState(existing?.mountPath ?? '');
  const [hostPath, setHostPath] = useState(existing?.hostPath ?? '');

  const save = useMutation({
    mutationFn: () => {
      const payload = { name, mountPath, hostPath: hostPath || null };
      return existing
        ? api.patch(`${apiBase}/storages/${existing.uuid}`, payload)
        : api.post(`${apiBase}/storages`, payload);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey });
      onDone();
    },
  });

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <div>
        <Label htmlFor="st-name">Volume name</Label>
        <Input id="st-name" required value={name} onChange={(e) => setName(e.target.value)} placeholder="uploads" />
      </div>
      <div>
        <Label htmlFor="st-mount">Mount path (in the container)</Label>
        <Input
          id="st-mount"
          required
          value={mountPath}
          onChange={(e) => setMountPath(e.target.value)}
          placeholder="/data/uploads"
        />
      </div>
      <div>
        <Label htmlFor="st-host">Host path (optional bind mount)</Label>
        <Input
          id="st-host"
          value={hostPath}
          onChange={(e) => setHostPath(e.target.value)}
          placeholder="Leave empty for a named volume"
        />
      </div>
      {save.error != null && <ErrorNote error={save.error} />}
      <p className="text-xs text-slate-500 dark:text-slate-400">
        Storage changes apply on the next deployment.
      </p>
      <div className="flex justify-end">
        <Button type="submit" loading={save.isPending}>
          {existing ? 'Save changes' : 'Add storage'}
        </Button>
      </div>
    </form>
  );
}

export function StoragePanel({
  apiBase,
  queryKey,
  enabled,
}: {
  apiBase: string;
  queryKey: unknown[];
  enabled: boolean;
}) {
  const queryClient = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState<StorageDto | null>(null);

  const storages = useQuery({
    queryKey,
    queryFn: () => api.get<StorageDto[]>(`${apiBase}/storages`),
    enabled,
  });
  const remove = useMutation({
    mutationFn: (storageUuid: string) => api.delete(`${apiBase}/storages/${storageUuid}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey }),
  });

  if (storages.isPending) return <Spinner />;
  if (storages.error) return <ErrorNote error={storages.error} />;

  return (
    <Card>
      <CardHeader
        title="Persistent storage"
        description="Named volumes and bind mounts that survive deployments"
        actions={
          <Button onClick={() => setAddOpen(true)}>
            <Plus className="size-4" /> Add storage
          </Button>
        }
      />
      {remove.error != null && (
        <div className="px-5 pt-3">
          <ErrorNote error={remove.error} />
        </div>
      )}
      {storages.data.length === 0 ? (
        <EmptyState
          title="No persistent storage"
          description="Data written elsewhere in the container is lost on redeploy."
        />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500 dark:border-slate-800 dark:text-slate-400">
              <tr>
                <th className="px-5 py-3 font-medium">Name</th>
                <th className="px-5 py-3 font-medium">Mount path</th>
                <th className="px-5 py-3 font-medium">Host path</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {storages.data.map((storage) => (
                <tr key={storage.uuid}>
                  <td className="px-5 py-3 font-medium">{storage.name}</td>
                  <td className="px-5 py-3 font-mono text-xs">{storage.mountPath}</td>
                  <td className="px-5 py-3 font-mono text-xs text-slate-500 dark:text-slate-400">
                    {storage.hostPath ?? '—'}
                  </td>
                  <td className="whitespace-nowrap px-5 py-3 text-right">
                    <Button variant="ghost" onClick={() => setEditing(storage)} aria-label={`Edit ${storage.name}`}>
                      <Pencil className="size-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      className="hover:!text-red-600"
                      aria-label={`Delete ${storage.name}`}
                      onClick={() => {
                        if (window.confirm(`Delete storage "${storage.name}"? Data may be lost.`)) {
                          remove.mutate(storage.uuid);
                        }
                      }}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Modal title="Add persistent storage" open={addOpen} onClose={() => setAddOpen(false)}>
        <StorageForm apiBase={apiBase} queryKey={queryKey} onDone={() => setAddOpen(false)} />
      </Modal>
      <Modal title={`Edit ${editing?.name ?? ''}`} open={editing !== null} onClose={() => setEditing(null)}>
        {editing && (
          <StorageForm apiBase={apiBase} queryKey={queryKey} existing={editing} onDone={() => setEditing(null)} />
        )}
      </Modal>
    </Card>
  );
}
